import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { get } from 'lodash';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { BaseModalComponent } from 'common/src/modules/rnpl-common/components';
import { BundledProduct, ProductModel, ProductsService, RelatedProductModel } from '../../../products';
import { ProductTypes } from '../../../products/product-types';
import { SalesPosition, SalesPositionsByProductType } from 'projects/workspace/src/app/sales-order/models/sales-order-position.model';
import { SalesOrderApiService } from 'projects/workspace/src/app/sales-order/services/sales-order-api.service';
import { ResponseModel } from 'projects/workspace/src/app/shared/models/response';
import { CompositeProductModalComponent } from '../../modals-products';
import { CustomSearchFn } from 'common/src/modules/rnpl-common/helpers';
import { PRODUCTS_TYPE } from '../../../rnpl-common/constants/products-type';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { PurchaseOrderApiService } from 'projects/workspace/src/app/purchase-order/services/purchase-order-api.service';
import { TradeDocumentPositionModel } from 'projects/workspace/src/app/trade/models';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActionButtonsService } from '../../../../services/action-buttons.service';
import { RecalculatePositionApiService } from '../../../../services/recalculate-position-api.service';
import { DocumentTypesUppercaseEnum } from '../../modals-common/link-document-modal/enums/ducument-types.enum';
import { SalesOrderListTabsEnum } from 'projects/workspace/src/app/sales-order/enums';
import { ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { PositionGroupModel, ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import { PositionGroupingEnum, PositionMediaTypeEnum, PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { SalesOrderModel } from 'projects/workspace/src/app/sales-order/models/sales-order.model';
import { PositionMediaFormComponent } from 'projects/workspace/src/app/shared/forms/position-media-form/position-media-form.component';
import { PdfSettingsAlignEnum } from 'projects/workspace/src/app/pdf-settings/enums';

@Component({
  selector: 'rnpl-sales-order-position-modal',
  templateUrl: './sales-order-position-modal.component.html',
  styleUrls: ['./sales-order-position-modal.scss']
})
export class SalesOrderPositionModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
  public companyProfile: CompanyProfile;

  public bundledProducts: Partial<BundledProduct>[];
  public activeTab: 'OVERVIEW'|'BUNDLE_PRODUCTS' = 'OVERVIEW';
  public tabs = [
    {
      tabName: 'OVERVIEW',
      label: 'APP.POSITION'
    },
    {
      tabName: 'BUNDLE_PRODUCTS',
      label: 'BUNDLES.PRODUCTS_IN_BUNDLE'
    }
  ];

  public productTypeRadio: 'common'|'general' = 'common';
  public currentStep: 'positionInfo'|'relatedProducts' = 'positionInfo';

  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];
  public readonly productTypes = ProductTypes;
  public positionMediaTypeEnum = PositionMediaTypeEnum;
  public positionGroupingEnum = PositionGroupingEnum;
  public previousUnitType: string;
  public previousUpdatedPosition: any;

  public productsList: ProductModel[] = [];
  public positionsListFromPO: TradeDocumentPositionModel[] = [];
  public relatedProducts: RelatedProductModel[] = [];

  public isProductFromPO: boolean = false;
  public selectedProduct: ProductModel = null;
  public selectedPosition: TradeDocumentPositionModel | ProductModel = null;
  public selectedProductInvalid: boolean = false;
  public selectedPositionInvalid: boolean = false;
  public positionName: string = null;
  public positionNameInvalid: boolean = false;

  public marginTotal: number = null;
  public marginPercent: number = null;

  public customSearchFn = CustomSearchFn;

  public selectedProductType;
  @ViewChild('positionMediaFormComponent', { static: false }) positionMediaFormComponentRef: PositionMediaFormComponent;

  readonly generalProductsList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  readonly showDropdownSpin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly addPositionRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public form: FormGroup;

  constructor(
    public store: Store<AppState>,
    public toasterService: ToasterService,
    public dialog: MatDialog,
    private productsService: ProductsService,
    private salesOrderApi: SalesOrderApiService,
    private purchaseOrderApiService: PurchaseOrderApiService,
    private translate: TranslateService,
    public actionButtonsService: ActionButtonsService,
    public dialogRef: MatDialogRef<SalesOrderPositionModalComponent>,
    private fb: FormBuilder,
    private recalculatePositionApiService: RecalculatePositionApiService,
    private productUnitApiService: ProductUnitApiService,
    @Inject(MAT_DIALOG_DATA) public data: {
      salesOrder: SalesOrderModel;
      salesOrderId: number;
      salesOrderStatus: SalesOrderListTabsEnum;
      salesPriceListName: string,
      vatBlocked: boolean;
      groupId: string;
    }
  ) {
    super(toasterService);

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
      });
  }

  ngOnInit() {
    this.initForm();
    this.getProducts();
    // this.getGeneralProductsListDropdown();

    this.dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.actionButtonsService.hideSidebarHandler();
      });
  }

  private initForm(selectProduct: ProductModel = {} as ProductModel): void {
    this.form = this.fb.group({
      grossTotalPrice: [this.setCurrencyValue(get(selectProduct, 'prices.price.grossPrice')), Validators.required],
      grossUnitPrice: [this.setCurrencyValue(get(selectProduct, 'prices.price.grossPrice')), Validators.required],
      netTotalPrice: [this.setCurrencyValue(get(selectProduct, 'prices.price.netPrice')), Validators.required],
      netUnitPrice: [this.setCurrencyValue(get(selectProduct, 'prices.price.netPrice')), Validators.required],
      productDescription: [get(selectProduct, 'description')],
      providedServices: [get(selectProduct, 'description')],
      vat: [
        get(selectProduct, 'prices.price.vat') || get(selectProduct, 'prices.price.vat') === 0
          ? get(selectProduct, 'prices.price.vat')
          : get(selectProduct, 'accountingSettings.vatRateByDefault')
      ],
      discount: [get(selectProduct, 'prices.price.discount')],
      discountAmountNet: [this.setCurrencyValue(get(selectProduct, 'prices.price.discountNet'))],
      discountAmountGross: [this.setCurrencyValue(get(selectProduct, 'prices.price.discountGross'))],
      addDiscount: [!!get(selectProduct, 'prices.price.discount')],
      quantity: [1],
      addRemark: [false],
      remark: [''],
      unitType: [get(selectProduct, 'unitType') || 'pcs'],
    });
  }

  selectPositionChanges(selectedProduct: ProductModel): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.REGULAR);
    this.selectedProductType = selectedProduct.type || selectedProduct.productType;
    if (this.productTypeRadio === 'general') {
      this.selectedProductType = ProductTypes.GENERAL;
    }
    if (this.data.vatBlocked) {
      this.vat.disable({onlySelf: true, emitEvent: false});
      if (this.priceOptionGross) {
        this.netUnitPrice.disable({onlySelf: true, emitEvent: false});
        this.netTotalPrice.disable({onlySelf: true, emitEvent: false});
      }
      if (this.priceOptionNet) {
        this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
      }
    }

    this.getProductById(+selectedProduct.id);
    this.getProductUnits();
  }

  selectVariation(selectedVariation: ProductModel): void {
    this.selectedProduct = this.productsList.find(itm => +itm.id === +selectedVariation.productId);
    this.selectPositionChanges(this.selectedProduct);
  }

  public getProductUnits(): void {
    this.productUnitApiService.getUnits$(this.selectedProduct.type || this.selectedProduct.entityForm)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        this.unitsList = unitsList.map(u => u.name);
      });
  }

  selectPositionFromPoChanges(selectedProduct: ProductModel): void {
    this.selectedProductType = selectedProduct.productType;
    this.selectedPosition = selectedProduct;
    if (this.data.vatBlocked) {
      this.vat.disable({onlySelf: true, emitEvent: false});
    }
    this.getProductById(+selectedProduct.productId, true);
  }

  public getProductById(productId, fromPo: boolean = false) {
    this.productsService.getProduct(productId, this.data.salesPriceListName)
      .pipe(takeUntil(this._destroy))
      .subscribe(product => {
        this.initForm(product);
        this.setBundledProducts(product);
        if (this.data.vatBlocked) {
          const defaultPrice = this.setCurrencyValue(get(product, 'prices.price.netPrice'));
          this.vat.setValue(0);
          this.grossUnitPrice.setValue(defaultPrice);
          this.grossTotalPrice.setValue(defaultPrice);
        }

        // if (product.productType === ProductTypes.BUNDLE && get(product, 'prices.price.priceFromBundledProducts')) {
        //   this.netTotalPrice.disable({onlySelf: true, emitEvent: false});
        //   this.netUnitPrice.disable({onlySelf: true, emitEvent: false});
        //   this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
        //   this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        // }

        if (fromPo) {
          this.quantity.setValue(this.selectedPosition.quantity);
          this.description.setValue(this.selectedPosition.productDescription);
          this.selectedProduct = {
            ...product,
            productImgUrl: this.selectedPosition.productImgUrl,
            description: this.selectedPosition.productDescription
          };
        }

        this.relatedProducts = product.outgoingRelatedProducts.map(itm => ({
          ...itm,
          enabled: itm.necessarily
        }));

        this.marginTotal = null;
        this.marginPercent = null;
        this.updatePosition(1, 'quantity');
    });
  }

  public setBundledProducts(product: ProductModel): void {
    this.bundledProducts = [];
    if (product.bundledProducts) {
      this.bundledProducts = product.bundledProducts;
    }
  }

  public resetBundledProducts(): void {
    this.bundledProducts = [];
    this.activeTab = 'OVERVIEW';
  }

  // private getGeneralProductsListDropdown(): void {
  //   this.productsService.getGeneralProductsListDropdown(false)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((response) => {
  //       this.generalProductsList$.next(response.filter(glProduct => glProduct.isVisibleForList));
  //     });
  // }

  private getProducts(): void {
    this.showDropdownSpin$.next(true);
    this.productsService.getActiveProducts(undefined, undefined, true, this.data.salesPriceListName)
      .pipe(takeUntil(this._destroy))
      .subscribe((products: ProductModel[]) => {
        this.productsList = products.map(item => ({
          ...item,
          searchLabel: `${item.name} ${this.translatedProductType(item.type)} ${item.net_price || 'n/a'} ${item.available} ${item.runple_id} ${item.description}`
        }));
        this.showDropdownSpin$.next(false);
      });

    this.purchaseOrderApiService.getPositionsFromPurchaseOrders()
      .pipe(takeUntil(this._destroy))
      .subscribe((products) => {
        this.positionsListFromPO = products.map(item => ({
          ...item,
          searchLabel: `${item.description} ${item.orderRunpleId} ${this.translatedProductType(item.productType)} ${item.netUnitPrice} ${item.quantity} ${item.productRunpleId} ${item.productDescription}`
        }));
        this.showDropdownSpin$.next(false);
      });
  }

  public translatedProductType(productType: ProductTypes): string {
    return this.translate.instant(PRODUCTS_TYPE[productType]);
  }

  public isProductFromPOChanged(): void {
    this.selectedPosition = null;
    this.selectedProduct = null;
    this.selectedPositionInvalid = false;
  }

  public createProduct(productType: ProductTypes): void {
    const dialog = this.dialog.open(CompositeProductModalComponent, {
      data: {
        family: null,
        productType: productType
      },
      disableClose: true,
    });

    dialog.afterClosed().subscribe((product: ProductModel) => {
      if (product) {
        this.productsList.push(product);
        this.selectedProduct = product;
        this.selectPositionChanges(product);
        this.selectedProductType = product.productType;
        this.initForm(this.selectedProduct);
        this.setBundledProducts(product);
      }
    });
  }

  public displayTextPosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.TEXT);
    this.selectedProduct = {
      name: this.translate.instant('ECOMMERCE.TEXT'),
      icon: 'user-defined'
    } as any;
  }

  public displayImagePosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.IMAGE);
    this.selectedProduct = {
      name: this.translate.instant('FORM.IMAGE'),
      icon: 'image'
    } as any;
  }

  public displayCreditPosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.REGULAR);
    this.selectedProductType = ProductTypes.CREDIT;
    this.resetPricesControls();
    this.selectedProduct = { name: this.translate.instant('Credit position') };
    if (this.data.vatBlocked) {
      this.vat.setValue(0);
    } else {
      this.vat.setValue(null);
    }
  }

  public resetPricesControls(): void {
    this.netUnitPrice.patchValue(null);
    this.netTotalPrice.patchValue(null);
    this.grossUnitPrice.patchValue(null);
    this.grossTotalPrice.patchValue(null);
  }

  public addPosition(): void {
    if (this.addPositionRequest$.getValue()) { return; }
    this.addPositionRequest$.next(true);

    if (this.positionMediaFormComponentRef) {
      if (this.positionMediaFormComponentRef.mediaType === PositionMediaTypeEnum.TEXT) {
        this.addMediaPosition(PositionMediaTypeEnum.TEXT, {description: this.positionMediaFormComponentRef.textValue});
        return;
      }
      if (this.positionMediaFormComponentRef.mediaType === PositionMediaTypeEnum.IMAGE) {
        this.addMediaPosition(
          PositionMediaTypeEnum.IMAGE,
          {
            file: this.positionMediaFormComponentRef.images && this.positionMediaFormComponentRef.images[0],
            height: this.positionMediaFormComponentRef.heightControlValue as any,
            width: this.positionMediaFormComponentRef.widthControlValue as any,
            alignment: this.positionMediaFormComponentRef.alignmentControlValue as any,
            useAspectRatio: this.positionMediaFormComponentRef.useAspectRatioControlValue as any,
          }
        );
        return;
      }
    }

    this.addRegularPosition();
  }

  public addMediaPosition(
    mediaType: PositionMediaTypeEnum,
    data: {
      file?: File,
      description?: string,
      width?: number,
      height?: number,
      alignment?: PdfSettingsAlignEnum,
      useAspectRatio?: boolean,
    }
  ): void {
    data['groupId'] = this.data.groupId;
    this.salesOrderApi.createPositionMedia(this.data.salesOrder.id, mediaType, data)
      .pipe(finalize(() => this.addPositionRequest$.next(false)))
      .subscribe((response: PositionGroupModel[]) => this.dialogRef.close(response));
  }

  public addRegularPosition(): void {
    if (!this.selectedProduct && !this.selectedPosition) {
      this.selectedProductInvalid = true;
      this.selectedPositionInvalid = true;
      this.addPositionRequest$.next(false);
      return;
    }

    if (this.typeCredit) {
      if (!this.positionName) {
        this.positionNameInvalid = true;
        this.addPositionRequest$.next(false);
        return;
      }
    }

    const position: SalesPosition = {} as SalesPosition;

    if (this.isProductFromPO) {
      position.productId = this.selectedPosition.productId;
      position.poPositionId = this.selectedPosition.positionId;
    } else {
      position.productId = +this.selectedProduct.id;
    }

    this.salesOrderApi
      .createSalesOrderPosition(
        this.data.salesOrderId,
        {
          ...this.form.getRawValue(),
          ...position,
          grossTotalPrice: this.setCurrencyControl(this.grossTotalPrice.value),
          grossUnitPrice: this.setCurrencyControl(this.grossUnitPrice.value),
          netTotalPrice: this.setCurrencyControl(this.netTotalPrice.value),
          netUnitPrice: this.setCurrencyControl(this.netUnitPrice.value),
          productType: this.selectedProductType,
          positionType: this.typeCredit
            ? ProductTypes.CREDIT
            : null,
          positionName: this.typeCredit
            ? this.positionName
            : this.selectedProduct.name,
          productDescription: !this.typeServices && !this.typeGeneralService
            ? this.description.value
            : null,
          providedServices: this.typeServices || this.typeGeneralService
            ? this.providedServices.value
            : null,
          productRelatedIds: this.relatedProducts
            ? this.relatedProducts
              .filter(itm => itm.enabled)
              .map(itm => itm.id)
            : [],
          groupId: this.typeCredit
            ? null
            : this.data.groupId
        })
      .pipe(
        finalize(() => this.addPositionRequest$.next(false)),
        takeUntil(this._destroy)
      )
      .subscribe(this.subscriberHandler);
  }

  public subscriberHandler = (response: ResponseModel<SalesPositionsByProductType>) => {
    this.dialogRef.close(response);
  }

  public setCurrencyControl(val): number {
    if (!val) { return val; }
    return +(val * 100).toFixed(0);
  }

  public updatePosition(value, fieldName): void {
    this.addPositionRequest$.next(true);

    this.recalculatePositionApiService.recalculatePosition(DocumentTypesUppercaseEnum.SO, {
      protoPosition: {
        ...this.form.getRawValue(),
        salesPriceListName: this.data.salesPriceListName,
        productId: this.selectedProduct.id,
        productTypeForGrouping: this.selectedProductType,
        productDescription: !this.typeServices && !this.typeGeneralService
          ? this.description.value
          : null,
        priceFromBundledProducts: get(this.selectedProduct, 'price_from_bundled_products')
          || get(this.selectedProduct, 'prices.price.priceFromBundledProducts'),
        bundleType: get(this.selectedProduct, 'bundle_type')
          || get(this.selectedProduct, 'bundleType'),
        providedServices: this.typeServices || this.typeGeneralService
          ? this.providedServices.value
          : null,
        priceOption: this.data.salesOrder.preferences.priceOption,
      },
      fieldUpdateRequest: {
        fieldName: fieldName,
        fieldValue: value
      },
      type: this.selectedProductType
    })
      .pipe(
        finalize(() => this.addPositionRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe(product => {
        this.form.patchValue(product);
        this.previousUnitType = product.unitType;
        this.previousUpdatedPosition = product;
        this.marginPercent = product.marginPercent;
        this.marginTotal = product.marginTotal;
      }, (error) => {
        if (error && error.error && error.error.errorCodes) {
          if (error.error.errorCodes.length &&  error.error.errorCodes.includes(1786)) {
            if (this.previousUpdatedPosition) {
              this.grossTotalPrice.patchValue(this.previousUpdatedPosition.grossTotalPrice);
              this.grossUnitPrice.patchValue(this.previousUpdatedPosition.grossUnitPrice);
              this.netTotalPrice.patchValue(this.previousUpdatedPosition.netTotalPrice);
              this.netUnitPrice.patchValue(this.previousUpdatedPosition.netUnitPrice);
            }
          }
        }
        if (fieldName === 'unitType') {
          // set previous value of unit type in case of error
          this.unitTypeControl.patchValue(this.previousUnitType);
          // redraw quantity control view
          setTimeout(() => this.quantity.patchValue(this.quantity.value), 10);
        }
      });
  }

  public setCurrencyValue(value): number {
    if (!value) { return value; }
    return +(value / 1e2).toFixed(2);
  }

  public updateDescription(description: string): void {
    this.selectedProduct = {
      ...this.selectedProduct,
      description: description
    };
  }

  public updateDescriptionByProductType(): void {
    if (this.selectedProductType === ProductTypes.SERVICES || this.typeGeneralService) {
      this.updatePosition(this.providedServices.value, 'providedServices');
    } else {
      this.updatePosition(this.description.value, 'productDescription');
    }
  }

  get typeGeneral(): boolean {
    return this.selectedProductType === ProductTypes.GENERAL;
  }
  get typeCredit(): boolean {
    return this.selectedProductType === ProductTypes.CREDIT;
  }
  get typeGeneralService(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.entityForm === ProductTypes.SERVICES;
  }
  get typeServices(): boolean {
    return this.selectedProductType === ProductTypes.SERVICES;
  }
  get typeGoods(): boolean {
    return this.selectedProductType === ProductTypes.GOODS;
  }

  get isTemplate(): boolean {
    return this.data && this.data.salesOrderStatus === SalesOrderListTabsEnum.TEMPLATE;
  }

  get allowFractionalValues(): boolean {
    if (!this.unitTypeControl.value || !this.unitsListSource.length) { return true; }
    return this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.unitTypeControl.value)
      .allowFractionalValues;
  }

  get priceOptionNet(): boolean {
    return this.data.salesOrder.preferences.priceOption === PriceOptionEnum.NET || this.data.salesOrder.properties.smallBusiness;
  }
  get priceOptionGross(): boolean {
    return this.data.salesOrder.preferences.priceOption === PriceOptionEnum.GROSS && !this.data.salesOrder.properties.smallBusiness;
  }

  get mediaType(): PositionMediaTypeEnum {
    if (this.positionMediaFormComponentRef && this.positionMediaFormComponentRef.mediaType) {
      return this.positionMediaFormComponentRef.mediaType;
    }
    return PositionMediaTypeEnum.REGULAR;
  }

  get grossTotalPrice(): FormControl { return this.form.get('grossTotalPrice') as FormControl; }
  get grossUnitPrice(): FormControl { return this.form.get('grossUnitPrice') as FormControl; }
  get addRemark(): FormControl { return this.form.get('addRemark') as FormControl; }
  get netTotalPrice(): FormControl { return this.form.get('netTotalPrice') as FormControl; }
  get netUnitPrice(): FormControl { return this.form.get('netUnitPrice') as FormControl; }
  get description(): FormControl { return this.form.get('productDescription') as FormControl; }
  get providedServices(): FormControl { return this.form.get('providedServices') as FormControl; }
  get quantity(): FormControl { return this.form.get('quantity') as FormControl; }
  get remark(): FormControl { return this.form.get('remark') as FormControl; }
  get vat(): FormControl { return this.form.get('vat') as FormControl; }
  get discount(): FormControl { return this.form.get('discount') as FormControl; }
  get discountAmountNet(): FormControl { return this.form.get('discountAmountNet') as FormControl; }
  get discountAmountGross(): FormControl { return this.form.get('discountAmountGross') as FormControl; }
  get addDiscount(): FormControl { return this.form.get('addDiscount') as FormControl; }
  get unitTypeControl(): FormControl { return this.form.get('unitType') as FormControl; }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
